import React, { useEffect, useState } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Button, Card, Col, Descriptions, Row, Select, Space, Table, Tabs, Typography, Form } from 'antd';
import type { TabsProps } from 'antd';
import { useAppQuery } from '@/common/hooks/useAppQuery';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { get, post } from '@/common/services/httpClient';
import { useTranslation } from 'react-i18next';
import type {
    ClaimDetailType,
    coverageBalanceType,
    InvoiceLineItem,
    InvoiceType,
    PolicyHolder,
    StatusHistoryType,
} from '../../types/participantsTypes';
import type { ApiResponse } from '../../../auth/types/authTypes';
import StatusTag from '@/common/components/Status';
import FormatLocalDate from '@/common/components/FormatLocalDate';
import AmountFormat from '@/common/components/AmountFormat';
import {
    ReloadOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { CLAIM_STATUS_VALUE } from '../../../../common/staticData/options';
import { formatPaddedId } from '../../../../common/helpers/formatHelpers';
import { ClaimStatus, ClaimStatusName } from '../../../../common/staticData/enums';

// Fetch function
const fetchDetail = async (
    Id: string,
    cardNumber: string,
): Promise<PolicyHolder> => {
    const response = await get<ApiResponse<PolicyHolder>>(
        `${API_ENDPOINTS.PARTICIPANTS.GET_POLICYHOLDER_BY_DETAIL}?Id=${Id}&CardNumber=${cardNumber}`,
    );
    return response.data;
};

const Detail: React.FC = () => {
    const navigate = useNavigate();
    const { state } = useRouter().state.location;
    const [form] = Form.useForm();

    const record = (state as { record?: any })?.record;
    const policyHolderId = record?.id;
    
    const router = useRouter();
    const { data: policyHolderData, isLoading, isError } = useAppQuery(
        [QUERY_KEYS.PARTICIPANTS.GET_POLICYHOLDER_DETAIL, record?.id, record?.cardNumber],
        () => fetchDetail(record?.id, record?.cardNumber),
        { enabled: !!record?.id && !!record?.cardNumber  },
    );


    const { Title, Text } = Typography;
    const [filteredClaims, setFilteredClaims] = useState<ClaimDetailType[]>([]);
    const [isClaimFetching, setIsClaimFetching] = useState(false);
    const { t } = useTranslation();
    const onChange = (key: string) => console.log('Selected Tab:', key);

    useEffect(() => {
        handleFilterClaims()
    }, [form.getFieldValue('status')]);


    // Section container
    const Section = ({
        title,
        children,
    }: {
        title: string;
        children: React.ReactNode;
    }) => (
        <div
            style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: '24px',
                marginBottom: '24px',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
        >
            <h3 style={{ marginBottom: 16 }}>{title}</h3>
            {children}
        </div>
    );

    // Columns
    const historyColumns = [
        { title: t('Created By'), dataIndex: 'userName', key: 'userName' },
        {
            title: t('Created On'), dataIndex: 'createdOn', key: 'createdOn',
            render: (value: string) => <FormatLocalDate value={value} />,
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <StatusTag status={status} />
            ),
        },
    ];

    const coverageBalanceColumns = [
        { title: t('Service Category'), dataIndex: 'categoryName', key: 'categoryName',  },
        {
            title: t("Covered Amount"),
            dataIndex: "coveredAmount",
            key: "coveredAmount",
            align: "right",
            render: (value: number) => (
                <AmountFormat
                    value={value}
                    currencyCode={policyHolderData?.currencyISOCode}
                    amountInWord={false}
                />
            ),
        },
        {
            title: t("Approved Amount"),
            dataIndex: "approvedAmount",
            key: "approvedAmount",
            align: "right",
            render: (value: number) => (
                <AmountFormat
                    value={value}
                    currencyCode={policyHolderData?.currencyISOCode}
                    amountInWord={false}
                />
            ),
        },
        {
            title: t("Balance Amount"),
            dataIndex: "balanceAmount",
            key: "balanceAmount",
            align: "right",
            render: (_, record) => (
                <AmountFormat
                    value={record?.coveredAmount-record?.approvedAmount}
                    currencyCode={policyHolderData?.currencyISOCode}
                    amountInWord={false}
                />
            ),
        },
    ];

    // Line item columns
    const lineItemColumns = [
        { title: t("Name"), dataIndex: "name", key: "name" },
        { title: t("Age"), dataIndex: "age", key: "age" },
        { title: t("Gender"), dataIndex: "gender", key: "gender" },
        { title: t("Category"), dataIndex: "category", key: "category" },
        {
            title: t("Amount"),
            dataIndex: "amount",
            key: "amount",
            render: (value: number, record: any) => (
                <AmountFormat
                    value={value}
                    currencyCode={record?.currencyISOCode}
                    amountInWord={false}
                />
            ),
        },
        { title: t("Effective Date"), dataIndex: "effectiveDate", key: "effectiveDate", render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : '') },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <StatusTag status={status} />
            ),
        },
    ];

    const invoiceColumns = [
        { title: t("Plan"), dataIndex: "planName", key: "planName" },
        //{ title: t("Group"), dataIndex: "groupName", key: "groupName" },
        { title: t('Invoice Date'), dataIndex: 'invoiceDate', key: 'invoiceDate', render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : '') },
        { title: t('Due Date'), dataIndex: 'dueDate', key: 'dueDate', render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : '') },
        {
            title: t("Total Amount"),
            dataIndex: "totalAmount",
            key: "totalAmount",
            render: (value: number, record: InvoiceType) => (
                <AmountFormat
                    value={value}
                    currencyCode={record?.currencyISOCode}
                    amountInWord={false}
                />
            ),
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <StatusTag status={status} />
            ),
        },
    ];

    const claimGridColumns: ColumnsType<ClaimDetailType> = [
        {
        title: t("Claim No"), dataIndex: "claimno", key: "claimno", width: 100,
        render: (_, record) => (
            <Button
                type="link"
                onClick={() => {
                    router.navigate({
                        to: '/claim/requests/detail', // Corrected path
                        state: { record: record } as Record<string, unknown>,
                    });
                }}
            >
                {formatPaddedId(record.id)}
            </Button>
        )
        },
        { title: t("Reference No"), dataIndex: "referenceNo", key: "referenceNo", width: 120 },
        { title: t("Claim Date"), dataIndex: "claimDate", key: "claimDate", render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : ''), width: 120 },
        { title: t("Patient"), dataIndex: "patientName", key: "patientName", width: 150 },
        { title: t("Consultant"), dataIndex: "consultantName", key: "consultantName", width: 120 },
        { title: t("Medical Provider"), dataIndex: "medicalProviderName", key: "medicalProviderName", width:150 },
        {
            title: t("Total Amount "), dataIndex: "totalAmount", key: "totalAmount", width: 150,
            render: (value: number, record: any) => (
                <AmountFormat
                    value={value}
                    currencyCode={record?.currencyISOCode}
                    amountInWord={false}
                />
            ),
        },
        {
            title: t("Approved Amount "), dataIndex: "approvedAmount", key: "approvedAmount", width: 150,
            render: (value: number, record: any) => (
                <AmountFormat
                    value={value}
                    currencyCode={record?.currencyISOCode}
                    amountInWord={false}
                />
            ),
        },
        { title: t("Reason "), dataIndex: "reason", key: "reason", width: 250 },
        {
            title: t("Status"),
            dataIndex: "status",
            fixed: "right",
            key: "status",
            render: (status: string) => (
                <StatusTag status={ClaimStatusName[ClaimStatus[status as keyof typeof ClaimStatus]]} />
            ),
             width: 150 
        },
    ];

    // Layout sections
    const InfoRow = ({ data }: { data?: PolicyHolder }) => (
        <Descriptions
            bordered
            size="middle"
            column={{ xs: 1, sm: 2, md: 3 }}
            labelStyle={{
                fontWeight: 600,
                width: 150,           // ✅ fixed width for labels
                whiteSpace: "nowrap", // ✅ keep label text in one line
            }}
            contentStyle={{
                wordBreak: "break-word", // ✅ wrap long data text
            }}
            style={{ marginBottom: 24 }}
        >
            <Descriptions.Item label={t("Card Number")}>
                {data?.cardNumber || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Policy Code")}>
                {data?.policyCode || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Mobile No")}>
                {data?.mobileNo || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Address")}>
                {data?.address || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Country")}>
                {data?.country || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Status")}>
                <StatusTag status={data?.status} />
            </Descriptions.Item>


            <Descriptions.Item label={t("Coverage Amount")}>
                <AmountFormat
                    value={data?.totalAmount}
                    currencyCode={data?.currencyISOCode}
                    currencyName={data?.currencyISOCode}
                    amountInWord={false}
                />
            </Descriptions.Item>
            <Descriptions.Item label={t("Approved Amount")}>
                <AmountFormat
                    value={data?.approvedAmount}
                    currencyCode={data?.currencyISOCode}
                    currencyName={data?.currencyISOCode}
                    amountInWord={false}
                />
            </Descriptions.Item> 

            <Descriptions.Item label={t("Balance")}>
                <AmountFormat
                    value={
                        (data?.totalAmount ?? 0) - (data?.approvedAmount ?? 0)
                    }
                    currencyCode={data?.currencyISOCode}
                    currencyName={data?.currencyISOCode}
                    amountInWord={false}
                />
            </Descriptions.Item>

        </Descriptions>
    );

    const PlanRow = ({ data }: { data?: PolicyHolder }) => (
        <>
            <Section title={t('')}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <p><strong>{t("Company Name")}:</strong> {data?.companyName || 'N/A'}</p>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <p><strong>{t("Plan Name")}:</strong> {data?.planName || 'N/A'}</p>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <p><strong>{t("Category")}:</strong> {data?.category || 'N/A'}</p>
                    </Col>
                </Row>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <p>
                            <strong>{t("Premium Amount")}:</strong>{' '}
                            <AmountFormat value={data?.premiumAmount} currencyCode={data?.currencyISOCode} currencyName={data?.currencyISOCode} amountInWord={true} />
                        </p>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <p>
                            <strong>{t("Effective Date")}:</strong>{' '}
                            {data?.effectiveDate ? dayjs(data.effectiveDate).format('DD-MM-YYYY') : 'N/A'}
                        </p>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <p>
                            <strong>{t("Expiry Date")}:</strong>{' '}
                            {data?.expiryDate ? dayjs(data.expiryDate).format('DD-MM-YYYY')  : 'N/A'}
                        </p>
                    </Col>
                </Row>
            </Section>
        </>
    );

    const HistoryRow = ({ data }: { data?: StatusHistoryType[] }) => (
        <Section title={t('History')}>
            <Table columns={historyColumns} dataSource={data} pagination={false} />
        </Section>
    );

    const InvoicesLineRow = ({ data }: { data?: InvoiceType[] }) => (
        <Section title={t("Invoices")}>
            <Table
                columns={invoiceColumns}
                dataSource={data}
                rowKey="id"
                expandable={{
                    expandedRowRender: (record: InvoiceType) => (
                        <Table<InvoiceLineItem>
                            columns={lineItemColumns}
                            dataSource={record.lineItems}
                            rowKey="lineItemId"
                            pagination={{ pageSize: 10 }}
                            size="small"
                        />
                    ),
                    rowExpandable: (record) => record.lineItems?.length > 0,
                }}
                pagination={{ pageSize: 10 }}
            />
        </Section>
    );

    //Temperory Removed Dependent
    // ✅ Added for Dependent Add New
    //const handleAddNewDependent = () => {
    //    navigate({
    //        to: '/participants/policy-holder/DependentForm',
    //        state: {
    //            record: {
    //                ParentPolicyHolderId: record?.id,
    //                name: record?.name,
    //                category: record?.category,
    //                nationalIDNo: record?.nationalIDNo,
    //                parentNationalIDNo: record?.parentNationalIDNo,
    //                companyId: record?.companyId,
    //                companyName: record?.companyName,
    //                customerId: record?.customerId,
    //                customerName: record?.customerName
    //            },
    //        } as Record<string, unknown>
    //    });
    //};

    const resetFilter = async () => {
        form.resetFields();
        handleFilterClaims();
    };

    const handleFilterClaims = async () => {
        if (!policyHolderId) return;

        setIsClaimFetching(true);
        try {
            const status = form.getFieldValue('status');
            const response = await post<ApiResponse<ClaimDetailType[]>>(
                API_ENDPOINTS.PARTICIPANTS.GET_CLAIM_DETAIL,
                {
                    PolicyHolderId: policyHolderId,
                    Status: status || "", // empty = all
                    pageNumber: 1,
                    pageSize: 10,
                }
            );
            setFilteredClaims(response.data || []);
        } catch (error) {
            console.error("Error fetching claims:", error);
            setFilteredClaims([]);
        } finally {
            setIsClaimFetching(false);
        }
    };

    const CoverageBalance = ({
        data
    }: {
            data?: coverageBalanceType[];
    }) => (
        <Section title={t('Coverage Balance')}>
            <Table
                columns={coverageBalanceColumns}
                dataSource={data}
                pagination={false}
                rowKey={(record) => record.name ?? undefined}
                onRow={(record) => {
                    const balance =
                        (record?.coveredAmount || 0) - (record?.approvedAmount || 0);

                    return {
                        style:
                            balance === 0
                                ? { backgroundColor: '#fff1f0' } // change color here
                                : {}
                    };
                }}
            />
        </Section>
    );

    // ✅ Tabs (with Claim Tab added)
    const items: TabsProps['items'] = [
        {
            key: '1',
            label: t('Plan Info'),
            children: <PlanRow data={policyHolderData} />,
        },
        {
            key: '2',
            label: t('History'),
            children: <HistoryRow data={policyHolderData?.statusHistory} />,
        },
        {
            key: '3',
            label: t('Invoice'),
            children: <InvoicesLineRow data={policyHolderData?.invoiceResponse} />,
            forceRender: true,
        },
        {
            key: "4",
            label: t("Claim"),
            children: (
                <Section title={t("Claim")}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                        <Form form={form} initialValues={{ status: undefined }}>
                            <Space >
                                <Form.Item name="status" noStyle>
                                    <Select
                                        placeholder={t("Select Status")}
                                        allowClear
                                        style={{ width: 200 }}
                                        options={CLAIM_STATUS_VALUE}
                                    />
                                </Form.Item>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleFilterClaims}
                                >
                                </Button>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={resetFilter}
                                >
                                </Button>
                            </Space>
                        </Form>
                    </div>

                    {isClaimFetching && <p>Loading claims...</p>}
                    {!isClaimFetching && (
                        <div style={{ maxHeight: 500, overflowY: "auto" }}>
                            <Table
                                columns={claimGridColumns}
                                dataSource={
                                    filteredClaims
                                }
                                rowKey="claimNo"
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 1200 }}
                                sticky
                            />
                        </div>
                    )}
                </Section>
            ),
        },
        ...(policyHolderData && policyHolderData?.coverageBalance?.length > 0 
            ? [
                {
                    key: '5',
                    label: t('Coverage Balance'),
                    children: (
                        <CoverageBalance
                            data={policyHolderData?.coverageBalance}
                        />
                    ),
                    forceRender: true,
                },
            ]
            : []),
    ];

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button
                    htmlType="button"
                    onClick={() => navigate({ to: '..' })}
                >
                    {t('common.goBack', { defaultValue: 'Go Back' })}
                </Button>
            </div>
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <Space size="small">
                            {policyHolderData?.name || t("Policy Holder Detail")}
                            <StatusTag status={policyHolderData?.status} />
                        </Space>
                    </Title>
                    <Space size="small" wrap>
                        <Text>{policyHolderData?.customerName || "Individual"}, </Text>
                        <Text>{policyHolderData?.companyName || "Individual"}</Text>
                    </Space>
                </div>
            </Card>

            {isLoading && <p>Loading...</p>}
            {isError && <p>Error fetching data</p>}

            {!isLoading && !isError && (
                <>
                    <InfoRow data={policyHolderData} />
                    <Tabs
                        defaultActiveKey="1"
                        items={items}
                        onChange={onChange}
                        destroyInactiveTabPane={false}
                    />
                </>
            )}
        </div>
    );
};

export default Detail;
